import { useEffect, useState, useCallback } from "react";
import { Search, Loader2, Building2, SlidersHorizontal } from "lucide-react";
import { Layout } from "@/components/Layout";
import { BusinessCard } from "@/components/BusinessCard";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

export default function Directory() {
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [loading, setLoading] = useState(true);

  const fetchBusinesses = useCallback(async (searchVal, cat) => {
    setLoading(true);
    try {
      const { data } = await api.get("/businesses", { params: { search: searchVal || undefined, category: cat } });
      setBusinesses(data);
    } catch (e) {
      setBusinesses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data.categories)).catch((e) => console.error("Failed to load categories", e));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchBusinesses(search, activeCat), 300);
    return () => clearTimeout(t);
  }, [search, activeCat, fetchBusinesses]);

  return (
    <Layout>
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Business Directory</span>
          <h1 className="mt-3 font-display font-black tracking-tight text-3xl sm:text-4xl text-slate-900">
            Discover verified businesses
          </h1>
          <p className="mt-3 text-slate-600 max-w-xl">Search by name, city, or category to find and connect with trusted partners across India.</p>

          <div className="mt-8 relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <Input
              data-testid="directory-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search businesses, cities, categories…"
              className="h-14 pl-12 pr-4 text-base bg-white border-slate-200 rounded-xl shadow-sm focus-visible:ring-brand/20"
            />
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
        {/* Category filters */}
        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          <SlidersHorizontal size={16} className="text-slate-400 shrink-0" />
          <button
            data-testid="cat-filter-all"
            onClick={() => setActiveCat("all")}
            className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${activeCat === "all" ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
          >
            All
          </button>
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`cat-filter-${c}`}
              onClick={() => setActiveCat(c)}
              className={`shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${activeCat === c ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="min-h-[30vh] flex items-center justify-center">
            <Loader2 className="animate-spin text-brand" size={32} />
          </div>
        ) : businesses.length === 0 ? (
          <div data-testid="directory-empty" className="min-h-[30vh] flex flex-col items-center justify-center text-center">
            <div className="h-16 w-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
              <Building2 className="text-slate-400" size={28} />
            </div>
            <p className="font-display font-bold text-lg text-slate-700">No businesses found</p>
            <p className="text-slate-500 mt-1">Try a different search or category.</p>
          </div>
        ) : (
          <div data-testid="directory-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {businesses.map((b) => (
              <BusinessCard key={b.id} business={b} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
